/**
 * @file RisksViewTab Component
 * @description Read-only view tab for displaying negative impacts/risks.
 * Used in the Investment Detail page to show risks without editing capability.
 */

import { useNegativeImpacts } from '@medsuite/api-client';
import { Card } from '@medsuite/ui-components';

import type { Investment, NegativeImpactWithCategory } from '@medsuite/models';

interface RisksViewTabProps {
  investment: Investment;
}

// Amount frequency labels for display
const AmountFrequencyLabel: Record<string, string> = {
  O: 'One-Time',
  M: 'Monthly',
  A: 'Annually',
};

/**
 * Format currency helper
 */
const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format number helper
 */
const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return new Intl.NumberFormat('en-US').format(value);
};

export function RisksViewTab({ investment }: RisksViewTabProps) {
  const investmentId = investment?.investment_id ?? 0;
  const isValidId = investmentId > 0 && !isNaN(investmentId);

  // Fetch negative impacts
  const { data: negativeImpacts = [], isLoading } = useNegativeImpacts(investmentId, {
    enabled: isValidId,
  });

  // Calculate totals
  const totalClientsAtRisk = negativeImpacts.reduce(
    (sum: number, impact: NegativeImpactWithCategory) => sum + (impact.clients_at_risk || 0),
    0
  );
  const totalArrAtRisk = negativeImpacts.reduce(
    (sum: number, impact: NegativeImpactWithCategory) => sum + (impact.arr_at_risk || 0),
    0
  );
  const totalAmount = negativeImpacts.reduce(
    (sum: number, impact: NegativeImpactWithCategory) => sum + (impact.amount || 0),
    0
  );

  // Guard: Show loading state if investment ID is invalid
  if (!isValidId) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-500 dark:text-gray-400">Loading investment data...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-500 dark:text-gray-400">Loading risks...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Negative Impacts / Risks
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Consequences if this investment is NOT made
            </p>
          </div>

          {negativeImpacts.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-gray-400 mb-4">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                No negative impacts documented for this investment.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Clients at Risk
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        ARR at Risk
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {negativeImpacts.map((impact: NegativeImpactWithCategory) => (
                      <tr key={impact.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-4">
                          <span className="px-2 py-0.5 rounded text-xs font-medium text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30">
                            {impact.category_name}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {impact.title || '—'}
                          </div>
                          {impact.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                              {impact.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-right">
                          {impact.amount ? (
                            <div>
                              <span className="font-mono text-red-600 dark:text-red-400">
                                {formatCurrency(impact.amount)}
                              </span>
                              {impact.amount_frequency && (
                                <span className="text-xs text-gray-400 block">
                                  {AmountFrequencyLabel[impact.amount_frequency]}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-right font-mono text-gray-900 dark:text-gray-100">
                          {formatNumber(impact.clients_at_risk)}
                        </td>
                        <td className="px-4 py-4 text-sm text-right font-mono text-red-600 dark:text-red-400">
                          {formatCurrency(impact.arr_at_risk)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-3">
                  Risk Summary
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Total Risks
                    </div>
                    <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
                      {negativeImpacts.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Total Amount
                    </div>
                    <div className="mt-1 text-xl font-bold text-red-600 dark:text-red-400">
                      {formatCurrency(totalAmount)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Clients at Risk
                    </div>
                    <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
                      {formatNumber(totalClientsAtRisk)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      ARR at Risk
                    </div>
                    <div className="mt-1 text-xl font-bold text-red-600 dark:text-red-400">
                      {formatCurrency(totalArrAtRisk)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

export default RisksViewTab;
